import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Scatter } from 'react-chartjs-2';

import { Chart, CategoryScale, LinearScale, PointElement, LineElement, RadialLinearScale, Plugin } from 'chart.js';

import { MeasuringSystem, SessionExtra, ShotExtra, Target } from '../../store/Session/SessionModel';
import sessionSlice from '../../store/Session/SessionSlice';
import { clubOrder, getClubId, getClubName, getClubColor, getClubTypeAbbreviation } from '../../config/ClubInfo';
import { formatTargetDistance } from '../../store/Session/helper/UnitConversions';

import { chartXYFromShot } from './chartXYFromShot';
import { customPointStyle } from './getCustomPointStyle';
import { generateCircularYGrid, generateCenterXGrid } from './generateGrids';
import { generateDispersionEllipse, getDispersionEllipse } from './getDispersionEllipse';

import './style.css';

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, RadialLinearScale);

const lightenColor = (hex: string) => `${hex}80`;

interface Props {
  sessionData: SessionExtra;
}

/** Match canvas plugin: ellipse only informs scale when 3+ plotted shots */
const DISPERSION_MIN_SHOTS_FOR_SCALE_ELLIPSE = 3;

/** Drill / combine sessions: one dispersion group per active target (+ club), not aggregated by club only */
const useDrillTargetDispersion = (sessionData: SessionExtra): boolean =>
  Boolean(sessionData.drillTargets?.length && sessionData.targets?.length);

/** Larger distance sorts first; combined with fractional targetOrdinal for stable ordering */
const DRILL_DATASET_ORDER_DISTANCE_SCALE = 1_000_000;

function clubTypeSortIndex(clubType: string): number {
  const i = clubOrder.indexOf(clubType);
  return i === -1 ? clubOrder.length : i;
}

/** Valid scatter points */
function scatterPointsFromShots(shots: ShotExtra[]) {
  return shots
    .filter(shot => shot.isActive && shot.carryDistanceValid && shot.sideValid)
    .map(shot => {
      const xy = chartXYFromShot(shot);
      return xy ? { xy, shot } : null;
    })
    .filter((d): d is { xy: { x: number; y: number }; shot: ShotExtra } => d !== null);
}

interface ScatterDataset {
  label: string;
  clubIcon: string;
  order: number;
  backgroundColor: string;
  clubColor: string;
  pointStyle: typeof customPointStyle;
  data: Array<{
    x: number;
    y: number;
    isFavorite: boolean;
    isSelected: boolean;
    clubType: string;
    clubId: string;
    shotIndex: number;
  }>;
}

/** Build datasets grouped by aggregated club (range / outdoor — existing behavior). */
function buildClubModeDatasets(
  sessionData: SessionExtra,
  minMaxRef: { v: number },
  maxYRef: { v: number },
): { datasets: ScatterDataset[]; ellipses: ReturnType<typeof getDispersionEllipse>[] } {
  const ellipses: ReturnType<typeof getDispersionEllipse>[] = [];

  const datasets = sessionData.clubs
    .filter(club => club.isActive)
    .map(club => {
      const shotsWithXY = scatterPointsFromShots(club.shots);

      shotsWithXY.forEach(({ xy }) => {
        if (Math.abs(xy.x) > minMaxRef.v) minMaxRef.v = Math.abs(xy.x);
        if (xy.y > maxYRef.v) maxYRef.v = xy.y;
      });

      const shotsForEllipse = shotsWithXY.map(({ xy }) => xy);
      if (shotsForEllipse.length >= DISPERSION_MIN_SHOTS_FOR_SCALE_ELLIPSE) {
        ellipses.push(getDispersionEllipse(shotsForEllipse));
      }

      return {
        label: club.clubName,
        clubIcon: club.clubIcon,
        order: -club.selectedTimestamp,
        backgroundColor: sessionData.isAnyShotSelected ? lightenColor(club.clubColor) : club.clubColor,
        clubColor: club.clubColor,
        pointStyle: customPointStyle,
        data: shotsWithXY.map(({ xy, shot }) => ({
          x: xy.x,
          y: xy.y,
          isFavorite: shot.isFavorite,
          isSelected: shot.isSelected,
          clubType: shot.clubType,
          clubId: club.clubId,
          shotIndex: shot.shotIndex,
        })),
      };
    });

  return { datasets, ellipses };
}

/**
 * Drill mode: separate scatter datasets per drill target (+ club bucket), same club at 70y / 80y / 90y => 3 datasets / 3 ellipses when each has 3+ valid shots.
 */
function buildDrillTargetModeDatasets(
  sessionData: SessionExtra,
  minMaxRef: { v: number },
  maxYRef: { v: number },
): { datasets: ScatterDataset[]; ellipses: ReturnType<typeof getDispersionEllipse>[] } {
  const ellipses: ReturnType<typeof getDispersionEllipse>[] = [];
  const datasets: ScatterDataset[] = [];

  sessionData.targets
    .filter((t: Target) => t.isActive)
    .forEach((target: Target, targetOrdinal: number) => {
      const drillDist = target.drillTarget?.distance?.distance ?? 0;
      const yardLabel = formatTargetDistance(drillDist, sessionData.measuringSystem);

      const byClub = new Map<string, ShotExtra[]>();
      for (const shot of target.shots) {
        if (!shot.isActive) continue;
        const cid = getClubId(shot);
        if (!byClub.has(cid)) byClub.set(cid, []);
        byClub.get(cid)!.push(shot);
      }

      const clubKeysSorted = Array.from(byClub.keys()).sort(
        (a, b) =>
          clubTypeSortIndex(byClub.get(a)![0].clubType) - clubTypeSortIndex(byClub.get(b)![0].clubType),
      );

      clubKeysSorted.forEach((clubKey, clubBucketIndex) => {
        const shots = byClub.get(clubKey)!;
        const first = shots[0];
        const shotsWithXY = scatterPointsFromShots(shots);
        if (shotsWithXY.length === 0) return;

        shotsWithXY.forEach(({ xy }) => {
          if (Math.abs(xy.x) > minMaxRef.v) minMaxRef.v = Math.abs(xy.x);
          if (xy.y > maxYRef.v) maxYRef.v = xy.y;
        });

        const shotsForEllipse = shotsWithXY.map(({ xy }) => xy);
        if (shotsForEllipse.length >= DISPERSION_MIN_SHOTS_FOR_SCALE_ELLIPSE) {
          ellipses.push(getDispersionEllipse(shotsForEllipse));
        }

        const clubColorHex = getClubColor(first.clubType, first.clubColor ?? '');
        const displayName = first.clubName || getClubName(first.clubType);
        const label = `${yardLabel} · ${displayName}`;

        const orderBias = -((drillDist + targetOrdinal / 100) * DRILL_DATASET_ORDER_DISTANCE_SCALE + clubBucketIndex);

        datasets.push({
          label,
          clubIcon: getClubTypeAbbreviation(first.clubType),
          order: orderBias,
          backgroundColor: sessionData.isAnyShotSelected ? lightenColor(clubColorHex) : clubColorHex,
          clubColor: clubColorHex,
          pointStyle: customPointStyle,
          data: shotsWithXY.map(({ xy, shot }) => ({
            x: xy.x,
            y: xy.y,
            isFavorite: shot.isFavorite,
            isSelected: shot.isSelected,
            clubType: shot.clubType,
            clubId: getClubId(shot),
            shotIndex: shot.shotIndex,
          })),
        });
      });
    });

  return { datasets, ellipses };
}

const ShotDispersionChart = ({ sessionData }: Props): JSX.Element => {
  const ref = useRef();
  const dispatch = useDispatch();

  const minMaxRef = { v: 5 };
  const maxYRef = { v: 0 };

  const drillMode = useDrillTargetDispersion(sessionData);

  const { datasets, ellipses } = drillMode
    ? buildDrillTargetModeDatasets(sessionData, minMaxRef, maxYRef)
    : buildClubModeDatasets(sessionData, minMaxRef, maxYRef);

  let minMax = minMaxRef.v;
  let maxY = maxYRef.v;

  const data = {
    labels: [sessionData.measuringSystem ?? MeasuringSystem.imperial],
    datasets,
  };

  // Iterate through ellipses (data-domain) and update min/max values
  for (const ellipse of ellipses) {
    for (let i = 0; i < 2 * Math.PI; i += 0.01) {
      const y =
        ellipse.center.y +
        ellipse.majorAxis * Math.cos(i) * Math.sin(ellipse.angle) +
        ellipse.minorAxis * Math.sin(i) * Math.cos(ellipse.angle);
      maxY = Math.max(maxY, y);
    }
  }

  // The x half-range keeps a ~3:1 down-range aspect (maxY / 3), but must never be
  // smaller than the actual lateral spread (minMaxRef.v) or shots would be clipped
  // off-screen. This happens in Normalized mode when a shot without normalized
  // values collapses maxY, shrinking maxY / 3 below the real |side| (KWSA-27).
  const aspectHalfWidth = maxY > 0 && Number.isFinite(maxY) ? maxY / 3 : 0;
  minMax = Math.max(aspectHalfWidth, minMaxRef.v) || 5;

  let options: any = {
    animation: false,
    layout: {
      autoPadding: false,
    },
    maintainAspectRatio: false,
    borderWidth: 4,
    radius: 15,
    scales: {
      x: {
        min: minMax * -1,
        max: minMax,
        grid: {
          display: false,
          color: 'white',
        },
        ticks: {
          display: true,
          color: '#131314',
          stepSize: 10,
          padding: -30,
        },
        border: {
          display: true,
          color: '#494A4C',
        },
      },
      y: {
        position: 'center',
        min: 0,
        max: Math.max(maxY * 1.03, 40),
        grid: {
          display: false,
          color: 'white',
        },
        ticks: {
          display: false,
          stepSize: 50,
        },
        border: {
          display: false,
        },
      },
    },
    onClick: (event: any, chartElements: any) => {
      if (chartElements.length > 0) {
        const clickedElement = chartElements[0];
        if (clickedElement) {
          let clubId = clickedElement?.element?.$context?.raw?.clubId || undefined;
          let shotIndex = clickedElement?.element?.$context?.raw?.shotIndex;
          let isSelected = clickedElement?.element?.$context?.raw?.isSelected;
          if (clubId) {
            dispatch(
              sessionSlice.actions.setClubShotIsSelected({
                isSelected: !isSelected,
                clubId: clubId,
                shotIndex: shotIndex,
              }),
            );
          }
        }
      }
    },
  };

  let plugins: Plugin[] = [
    {
      id: 'CircularYGrid',
      beforeDraw: generateCircularYGrid,
    },
    {
      id: 'CenterXGrid',
      beforeDraw: generateCenterXGrid,
    },
    {
      id: 'DispersionEllipse',
      beforeDraw: generateDispersionEllipse,
    },
  ];

  return <Scatter data={data} options={options} plugins={plugins} ref={ref} />;
};

export default ShotDispersionChart;
